import { parseISO } from "date-fns";
import { useMemo } from "react";

import type { CampaignJobBoardFormInput } from "../components/CampaignJobBoardForm/types";

import {
  getDaysRemainingInMonth,
  getLastDayOfNextMonth,
  getMinEndDate,
  getToday,
  isDateInCurrentMonth,
} from "@/utils/dateUtils";

export interface UseDateConstraintsParams {
  formValues: CampaignJobBoardFormInput;
  isEdit: boolean;
  isIndeed?: boolean;
}

export interface UseDateConstraintsReturn {
  dateConstraints: {
    minStartDate: Date;
    maxStartDate: Date;
    minEndDate: Date;
  };
  showCurrentMonthBudget: boolean;
  daysRemainingInMonth: number | null;
}

export function useDateConstraints({
  formValues,
  isEdit,
  isIndeed,
}: UseDateConstraintsParams): UseDateConstraintsReturn {
  const today = useMemo(() => {
    const date = getToday();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const lastDayOfNextMonth = useMemo(() => getLastDayOfNextMonth(), []);

  const dateConstraints = useMemo(() => {
    const maxStartDate = formValues.endDate
      ? new Date(new Date(formValues.endDate).getTime() - 24 * 60 * 60 * 1000)
      : lastDayOfNextMonth;
    const minStartDate = formValues.endDate
      ? today < new Date(formValues.endDate)
        ? today
        : new Date(formValues.endDate)
      : today;
    const baseMinEndDate = getMinEndDate(formValues.startDate, isEdit);
    let minEndDate = baseMinEndDate;
    if (isIndeed) {
      const centralDateStr = new Date().toLocaleDateString("en-CA", {
        timeZone: "America/Chicago",
      });
      const [y, m, d] = centralDateStr.split("-").map(Number);
      const centralTomorrow = new Date(y, m - 1, d + 1);
      minEndDate = new Date(Math.max(baseMinEndDate.getTime(), centralTomorrow.getTime()));
    }

    return {
      minStartDate,
      maxStartDate,
      minEndDate,
    };
  }, [formValues.endDate, formValues.startDate, isEdit, isIndeed, lastDayOfNextMonth, today]);

  const showCurrentMonthBudget =
    !isEdit &&
    formValues.startDate !== undefined &&
    formValues.startDate !== "" &&
    isDateInCurrentMonth(parseISO(formValues.startDate));

  const daysRemainingInMonth = showCurrentMonthBudget
    ? getDaysRemainingInMonth(parseISO(formValues.startDate))
    : null;

  return {
    dateConstraints,
    showCurrentMonthBudget,
    daysRemainingInMonth,
  };
}
